import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_AD_RECCO_DETAIL_QUERY_KEY } from "../../../utils/constants/queryKeys";
import CustomModal from "../../common/CustomModal";
import ButtonLoader from "../../Icons/ButtonLoader";
import { Button } from "../../ui/button";
import StatusChip from "../../common/StatusChip";

const LIMIT = 15;

const StatusBadge = ({ status }) => <StatusChip subtle status={status} />;

const AdReccoDetailModal = ({
  open,
  onClose,
  agencyId,
  adtypeId,
  adtypeName,
  deliveryDateFrom,
  deliveryDateTo,
}) => {
  const [page, setPage] = useState(0);
  const axiosPrivate = useAxiosPrivate();

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_AD_RECCO_DETAIL_QUERY_KEY, agencyId, adtypeId, deliveryDateFrom, deliveryDateTo, page],
    queryFn: () =>
      AdsAPIs.getReccoDetails({
        axiosPrivate,
        agency_id: agencyId,
        adtype_id: adtypeId,
        delivery_date_from: deliveryDateFrom,
        delivery_date_to: deliveryDateTo,
        limit: LIMIT,
        offset: page * LIMIT,
      }),
    enabled: open && agencyId != null && adtypeId != null,
  });

  const ads = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={adtypeName ? String(adtypeName) : "Recco details"}
      isButtonsRequired={false}
      className="!w-[95%] md:!w-[80%] lg:!w-[65%] !max-h-[90vh]"
    >
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px] gap-2">
          <ButtonLoader color="#7f7ad5" />
          <span className="text-gray-600 font-medium">Loading ads…</span>
        </div>
      ) : (
        <div className="flex flex-col gap-3 py-2">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500">Parent Ad ID</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500">Ad Name</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500">Adset</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500 text-center">Deliveries</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500 text-center">Mod Iterations</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500 text-center">Status</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500 text-center">Live</th>
                  <th className="py-3 px-3 text-xs font-semibold text-gray-500 text-right">Unit Cost</th>
                </tr>
              </thead>
              <tbody>
                {ads.length > 0 ? (
                  ads.map((ad) => (
                    <tr key={ad.ad_id} className="hover:bg-gray-50/50 transition-colors border-b border-gray-100 last:border-0">
                      <td className="py-3 px-3 text-sm text-gray-800 font-medium">{ad.ad_id}</td>
                      <td className="py-3 px-3 text-sm text-gray-700 max-w-[200px] truncate">{ad.ad_name || "—"}</td>
                      <td className="py-3 px-3 text-sm text-gray-600 max-w-[160px] truncate">{ad.adset_name || "—"}</td>
                      <td className="py-3 px-3 text-sm text-gray-600 text-center">{ad.delivery_count}</td>
                      <td className="py-3 px-3 text-sm text-gray-600 text-center">{ad.mod_iterations}</td>
                      <td className="py-3 px-3 text-center">
                        <StatusBadge status={ad.mod_status} />
                      </td>
                      <td className="py-3 px-3 text-sm text-center">
                        {ad.is_live ? (
                          <span className="text-green-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-700 text-right">
                        {ad.unit_cost != null ? `${ad.unit_cost} ${ad.currency || ""}`.trim() : "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400 italic">
                      No ads found for this combination.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalCount > LIMIT && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Showing {ads.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + ads.length, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-7 text-xs">
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * LIMIT + ads.length >= totalCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-7 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </CustomModal>
  );
};

export default AdReccoDetailModal;
